/**
 * QuickBooksExportDialog — connect to QuickBooks Online (org-level OAuth2) and
 * run a one-way payroll export (svdy -> QuickBooks) for a date window (ADR-041).
 *
 * Distinct from the messaging dialog (Connectors.tsx) and HrisConnectDialog:
 *   - Not connected → "Connect QuickBooks" hands off to the Intuit OAuth
 *     consent flow via /v1/integrations/quickbooks/oauth/start (?response=json).
 *   - Connected → shows realm/company + a date-range picker + "Preview" (dry
 *     run) and "Export now" buttons + recent run history.
 *
 * The export pushes one TimeActivity per worker-day in the window; unresolved
 * employees are reported (skip-on-miss), never guessed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  quickbooksApi,
  type QuickBooksConnectionStatus,
  type QuickBooksExportHistoryItem,
  type QuickBooksExportResult,
} from '../../api/integrations';
import type { ProviderDefinition } from './integrationCatalog';
import { ApiError } from '../../api/client';
import { translateApiError } from '../../i18n/translateApiError';
import { activeLocale } from '../../utils/datetime';

interface Props {
  provider: ProviderDefinition | null;
  onClose: () => void;
  onChanged: () => void;
}

// Default window = the current calendar month up to today (a sensible
// pay-period-close default; the admin adjusts either endpoint).
function defaultWindow(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(now) };
}

const QuickBooksExportDialog: React.FC<Props> = ({ provider, onClose, onChanged }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<QuickBooksConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [win, setWin] = useState(defaultWindow());
  const [result, setResult] = useState<QuickBooksExportResult | null>(null);
  const [history, setHistory] = useState<QuickBooksExportHistoryItem[]>([]);

  const open = provider?.key === 'quickbooks';

  const refresh = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const s = await quickbooksApi.connection();
      setStatus(s);
      if (s.connected) {
        setHistory((await quickbooksApi.history(10)).items);
      }
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setLoading(false);
    }
  }, [open, t]);

  useEffect(() => {
    if (open) {
      setResult(null);
      setWin(defaultWindow());
      refresh();
    }
  }, [open, refresh]);

  if (!provider) return null;

  const connected = !!status?.connected;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleConnect = () =>
    run(async () => {
      const resp = await quickbooksApi.oauthStart('/admin/integrations');
      if (resp?.auth_url) {
        window.location.href = resp.auth_url;
      }
    });

  const handleDisconnect = () =>
    run(async () => {
      await quickbooksApi.disconnect();
      onChanged();
      onClose();
    });

  const handleExport = (dry_run: boolean) =>
    run(async () => {
      const r = await quickbooksApi.export({
        start_date: win.start, end_date: win.end, dry_run,
      });
      setResult(r);
      if (!dry_run) {
        onChanged();
        setHistory((await quickbooksApi.history(10)).items);
      }
    });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('integrations:quickbooks.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t('integrations:quickbooks.subtitle')}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : connected ? (
            <>
              <Alert severity="success" variant="outlined">
                {t('integrations:quickbooks.connectedTo', {
                  realm: status?.realm_id ?? t('integrations:quickbooks.yourCompany'),
                })}
              </Alert>

              {status?.last_export_at ? (
                <Typography variant="caption" color="text.secondary">
                  {t('integrations:quickbooks.lastExport', {
                    time: new Date(status.last_export_at).toLocaleString(activeLocale()),
                  })}
                </Typography>
              ) : null}

              {/* Date-range picker (the export window). */}
              <Stack direction="row" spacing={1.5}>
                <TextField
                  label={t('integrations:quickbooks.startDate')}
                  type="date"
                  size="small"
                  fullWidth
                  value={win.start}
                  onChange={(e) => setWin((w) => ({ ...w, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label={t('integrations:quickbooks.endDate')}
                  type="date"
                  size="small"
                  fullWidth
                  value={win.end}
                  onChange={(e) => setWin((w) => ({ ...w, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              {/* Run summary. */}
              {result ? (
                <Alert
                  severity={result.errors.length > 0 ? 'warning' : 'success'}
                  variant="outlined"
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {result.dry_run
                      ? t('integrations:quickbooks.previewSummary')
                      : t('integrations:quickbooks.exportSummary')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {t('integrations:quickbooks.summaryLine', {
                      created: result.created,
                      updated: result.updated,
                      skipped: result.skipped_unchanged,
                    })}
                  </Typography>
                  {result.unresolved.length > 0 ? (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                      {t('integrations:quickbooks.unresolvedLine', {
                        count: result.unresolved.length,
                        names: result.unresolved.map((u) => u.worker_name).join(', '),
                      })}
                    </Typography>
                  ) : null}
                  {result.errors.length > 0 ? (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                      {t('integrations:quickbooks.errorsLine', { count: result.errors.length })}
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}

              {/* Recent runs. */}
              {history.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('integrations:quickbooks.recentRuns')}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {history.map((h) => (
                      <Box
                        key={h.id}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          fontSize: '0.75rem', color: 'text.secondary',
                        }}
                      >
                        <Chip
                          label={h.status ?? '—'}
                          size="small"
                          color={h.status === 'completed' ? 'success' : h.status === 'partial' ? 'warning' : 'default'}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', borderRadius: 1 }}
                        />
                        <span>
                          {t('integrations:quickbooks.runRecords', { count: h.records_count ?? 0 })}
                        </span>
                        <Box sx={{ flex: 1 }} />
                        <span>{h.created_at ? new Date(h.created_at).toLocaleString(activeLocale()) : ''}</span>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </>
          ) : (
            <Alert severity="info" variant="outlined">
              {t('integrations:quickbooks.notConnectedHint')}
            </Alert>
          )}

          {error ? <Alert severity="error" variant="outlined">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pb: 2, flexWrap: 'wrap', gap: 1 }}>
        {connected ? (
          <>
            <Button onClick={handleDisconnect} disabled={busy} color="error" size="small">
              {t('integrations:quickbooks.disconnect')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => handleExport(true)} disabled={busy} size="small">
              {t('integrations:quickbooks.preview')}
            </Button>
            <Button onClick={() => handleExport(false)} disabled={busy} variant="contained" size="small">
              {busy ? t('integrations:quickbooks.exporting') : t('integrations:quickbooks.exportNow')}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} size="small">
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={handleConnect} disabled={busy || loading} variant="contained" size="small">
              {t('integrations:quickbooks.connect')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuickBooksExportDialog;
